/**
 * Hand an .ics payload to the browser.
 *
 * This page is reached from a reservation email, so a large share of traffic
 * arrives inside a webview rather than a normal browser tab, and the two
 * engines fail in opposite directions:
 *
 * - WebKit (iOS Safari and every iOS in-app browser, which must use WKWebView)
 *   frequently drops `<a download>` on a blob URL. Navigating to a
 *   `data:text/calendar` URL instead opens the Calendar preview.
 * - Chromium has blocked top-frame navigation to `data:` URLs since Chrome 60,
 *   so the same trick is a silent no-op in Android WebView and the
 *   Chromium-based Instagram / Facebook / TikTok browsers.
 *
 * So the `data:` path is keyed on the engine, not on "is this a webview":
 * WebKit takes it, everything else takes the blob download.
 */
export function downloadIcs(ics: string, filename: string): void {
  if (isIos()) {
    window.location.href = `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
    return;
  }

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Revoking in the same tick cancels the download in Safari. The next tick is
  // late enough for every engine and still releases the blob immediately.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * iPadOS 13+ reports a desktop UA, so the touch-point check is what catches
 * modern iPads — `/iPad/` alone misses them.
 */
function isIos(): boolean {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
}
